import types from '../mutation-types';
import BulkActionsAPI from '../../api/bulkActions';
import ConversationAPI from '../../api/inbox/conversation';

export const state = {
  selectedConversationIds: [],
  uiFlags: {
    isUpdating: false,
    isMarkingRead: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getSelectedConversationIds(_state) {
    return _state.selectedConversationIds;
  },
};

export const actions = {
  async markRead({ state: currentState, commit, dispatch }) {
    if (currentState.uiFlags.isMarkingRead || currentState.uiFlags.isUpdating) {
      return null;
    }
    const ids = [...new Set(currentState.selectedConversationIds)];
    if (!ids.length) return null;
    const accountId = ConversationAPI.accountIdFromRoute;
    if (!accountId) return null;
    const result = { succeeded: 0, failed: 0, cancelled: false };
    commit(types.SET_BULK_ACTIONS_FLAG, { isMarkingRead: true });
    try {
      // Use the authorized per-conversation endpoint, with bounded request load.
      // Sequential requests bound load and allow cancellation between accounts.
      // eslint-disable-next-line no-restricted-syntax
      for (const id of ids) {
        if (ConversationAPI.accountIdFromRoute !== accountId) {
          result.cancelled = true;
          break;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await dispatch('markMessagesRead', { id, explicit: true }, { root: true });
          if (ConversationAPI.accountIdFromRoute !== accountId) {
            result.cancelled = true;
            break;
          }
          if (
            data.id !== id ||
            !Number.isFinite(data.agent_last_seen_at) ||
            !Number.isInteger(data.unread_count) ||
            data.unread_count < 0
          ) {
            throw new Error('Invalid read-state response');
          }
          commit(types.REMOVE_SELECTED_CONVERSATION_IDS, id);
          result.succeeded += 1;
        } catch (error) {
          // Keep failures selected; never silently retry uncertain requests.
          result.failed += 1;
        }
      }
      return result;
    } finally {
      commit(types.SET_BULK_ACTIONS_FLAG, { isMarkingRead: false });
    }
  },
  process: async function processAction({ commit }, payload) {
    commit(types.SET_BULK_ACTIONS_FLAG, { isUpdating: true });
    try {
      await BulkActionsAPI.create(payload);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_BULK_ACTIONS_FLAG, { isUpdating: false });
    }
  },
  setSelectedConversationIds({ commit }, id) {
    commit(types.SET_SELECTED_CONVERSATION_IDS, id);
  },
  removeSelectedConversationIds({ commit }, id) {
    commit(types.REMOVE_SELECTED_CONVERSATION_IDS, id);
  },
  clearSelectedConversationIds({ commit }) {
    commit(types.CLEAR_SELECTED_CONVERSATION_IDS);
  },
};

export const mutations = {
  [types.SET_BULK_ACTIONS_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },
  [types.SET_SELECTED_CONVERSATION_IDS](_state, ids) {
    // Check if ids is an array, if not, convert it to an array
    const idsArray = Array.isArray(ids) ? ids : [ids];

    // Concatenate the new IDs ensuring no duplicates
    _state.selectedConversationIds = [
      ...new Set([..._state.selectedConversationIds, ...idsArray]),
    ];
  },
  [types.REMOVE_SELECTED_CONVERSATION_IDS](_state, id) {
    _state.selectedConversationIds = _state.selectedConversationIds.filter(
      item => item !== id
    );
  },
  [types.CLEAR_SELECTED_CONVERSATION_IDS](_state) {
    _state.selectedConversationIds = [];
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
